import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { AnalyticsCommonApi, AnalyticsCommonTypes } from '@frontend/api-analytics';
import { getUser } from '@frontend/auth-helpers';
import { useAllAccessScopeStore, useAppScopeStore, useScopedQuery, WeaveLocation } from '@frontend/scope';
import { useHasAclAccess } from './use-has-acl-access';

type Props = {
  isAllAccessScopeEnabled?: boolean;
  isDemoAccount?: boolean;
  module?: AnalyticsCommonTypes.GetAnalyticsLocationsRequest['Module'];
};

type Response = {
  hasAccessToMultipleLocations?: boolean;
  hasDataSynced?: boolean;
  isLoadingLocations?: boolean;
  locationIds: string[];
  locationNames: Record<string, string>;
  preselectedAccessibleLocationIds: string[];
  accessibleLocationData: ReturnType<typeof useAppScopeStore>['accessibleLocationData'];
  accessibleLocationMap: Record<string, WeaveLocation>;
};

export const useAnalyticsOrgLocations = ({
  isAllAccessScopeEnabled,
  isDemoAccount = false,
  module,
}: Props): Response => {
  const user = getUser();
  const { accessibleLocationData, isSingleTypeScope, selectedLocationIds = [] } = useAppScopeStore();
  const { accessibleLocationMap } = useAllAccessScopeStore();

  const { locationIdsWithAclAccess } = useHasAclAccess({
    isAllAccessScopeEnabled,
    isDemoAccount,
    permissions: [Permission.ANALYTICS_READ],
  });

  const { data, isLoading } = useScopedQuery({
    queryKey: `analytics-org-locations-${module}-${isDemoAccount}-${selectedLocationIds.sort().join('-')}`,
    queryFn: () =>
      AnalyticsCommonApi.getAnalyticsLocations(
        {
          BGList: selectedLocationIds,
          IsMulti: !isSingleTypeScope,
          Module: module,
          OrgID: accessibleLocationData?.[selectedLocationIds[0]]?.groupId,
          UserId: user?.userID || '',
        },
        isDemoAccount
      ),
    enabled: !!module && !isAllAccessScopeEnabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { hasAccessToMultipleLocations, hasDataSynced, locationIds, locationNames, preselectedAccessibleLocationIds } =
    useMemo(() => {
      const locationsData = (isAllAccessScopeEnabled ? accessibleLocationMap : accessibleLocationData) ?? {};

      // For time being we still need to respect the old api for some modules
      // Hence keeping this condition.
      // TODO: Remove this condition when all modules are set to use accessible locations
      let userAccessibleLocationIds = locationIdsWithAclAccess;
      if (!isAllAccessScopeEnabled) {
        // Use data from the old API
        userAccessibleLocationIds = isDemoAccount ? selectedLocationIds : data?.ActiveLocations ?? [];
      }

      const { locationNames, preselectedAccessibleLocationIds } = userAccessibleLocationIds.reduce(
        (acc, locationId) => {
          if (selectedLocationIds.includes(locationId)) {
            acc.preselectedAccessibleLocationIds.push(locationId);
          }
          acc.locationNames[locationId] =
            locationsData[locationId]?.name || data?.ActiveLocationNames?.[locationId] || '-';

          return acc;
        },
        {
          locationNames: {} as Record<string, string>,
          preselectedAccessibleLocationIds: [] as string[],
        }
      );

      return {
        //  TODO: Revisit following condition(s) when we migrate PA to use all accesssible locations
        // hasDataSynced is applicable only to practice analytics and a new api will be created later
        hasDataSynced: isAllAccessScopeEnabled ? true : data?.SyncStatus,
        hasAccessToMultipleLocations: userAccessibleLocationIds.length > 1,
        locationIds: userAccessibleLocationIds,
        locationNames,
        preselectedAccessibleLocationIds,
      };
    }, [
      accessibleLocationData,
      accessibleLocationMap,
      data,
      isAllAccessScopeEnabled,
      isDemoAccount,
      locationIdsWithAclAccess,
      selectedLocationIds,
    ]);

  return {
    hasAccessToMultipleLocations,
    hasDataSynced,
    isLoadingLocations: isLoading,
    locationIds,
    locationNames,
    preselectedAccessibleLocationIds,
    accessibleLocationData,
    accessibleLocationMap,
  };
};
